import type {
  Direction,
  Meal,
  MealReasonCode,
  RecordActionMethod,
  ResolveScanResponse,
  SubmitMealResponse,
  SubmitPresenceResponse,
} from '@sentra/contracts';
import { isErrorCode, parsePassCode, roleHasPermission } from '@sentra/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Camera, CameraOff, UserRoundPlus } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { hasCode, isSessionGateRefusal } from '@/api/errors';
import { applyWriteEffects } from '@/api/invalidation';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage, useErrorCodeMessage } from '@/api/use-api-error';
import { useAppConfig } from '@/app/providers/config';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { useApiClient, useSession } from '@/features/auth/session';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import { platformMedia } from '@/features/persons/images/camera';
import type { DeviceStorage } from '@/features/persons/images/devices';
import type { Alarm, AnomalyDecision, AnomalyKind } from '@/features/scan/anomaly';
import { AnomalyDialog, browserAudio, createAlarm } from '@/features/scan/anomaly';
import { ScanCamera } from '@/features/scan/camera';
import type { LoadedDetector } from '@/features/scan/detector';
import { ScannerCheck, useScannerTrace } from '@/features/scan/diagnostics';
import { stageIntake } from '@/features/scan/handoff';
import { ScanIndicator } from '@/features/scan/indicator';
import { MealRepeatDialog } from '@/features/scan/meal-repeat';
import { ResolvePanel } from '@/features/scan/resolve';
import type { GateOutcome } from '@/features/scan/status';
import {
  GateStatusBanner,
  isAnomaly,
  outcomeDetail,
  outcomeOfFailure,
  outcomeOfSubmission,
} from '@/features/scan/status';
import { ScanUpload } from '@/features/scan/upload';
import { SCAN_OPT_OUT, SCAN_OPT_OUT_ATTRIBUTE, useScanCapture } from '@/features/scan/wedge';
import { useLocale } from '@/i18n';
import { useFormatters } from '@/i18n/use-format';

/**
 * The gate screen: one scan, one decision, one movement.
 *
 * **Four ways in, one path through.** A hardware wedge (AD-32), the terminal's camera, a
 * photograph of a band, and twenty-one characters typed by hand. Each produces a code and a
 * `method`, and the method is carried all the way to the row — it is what makes AD-53's
 * manual-versus-scan ratio mean anything, so a screen that filed everything as one value would
 * quietly destroy a metric the product reports.
 *
 * **The alarm is primed by the operator's own gesture, at the moment the code arrives.** Every
 * browser refuses audio from a page nobody has touched, and by the time an anomaly is known the
 * gesture that caused it is over; priming at the scan is what makes the alert audible on the
 * terminal where it matters. A keystroke burst, a press of Check and a click on the camera are all
 * user activation, which is why every entry point goes through one function.
 *
 * **An anomaly is pre-empted here and re-entered from the server's answer.** The direction the
 * resolve reported is what this screen compares against, so the reason picker opens before a
 * request is made rather than after a refusal — which is what makes "no movement is submitted
 * without a reason" a property of the screen rather than of the round trip. The server decides
 * again under the row lock (AD-20) and may disagree, because the state can move between the two:
 * `PRESENCE_REASON_REQUIRED` and a blocked person's recorded refusal therefore open the same dialog. The
 * disagreement IS the anomaly, and both paths end at the same place.
 *
 * **Nothing here is cached.** The resolve and the submission go through the client directly rather
 * than through the query cache, for the reason `band-field.tsx` already records: the cache key
 * would be a live bearer token held for the cache's lifetime, and the value would be a decrypted
 * person's summary held long after the person walked away.
 */

/**
 * How a code reached this screen, which is what the movement row records.
 *
 * `RecordActionMethod` rather than `SubmitPresenceRequest['method']`, which is the same five values
 * plus `api`. A browser is not a machine caller and this screen assigns only the five, so the wider
 * type bought nothing and cost the one thing that matters: the same value is handed to
 * `features/scan/handoff.ts` when the operator opens a record from a scan, and a record action has
 * no `api`. Narrowing here is what makes that hand-off type-check without a cast.
 */
type ScanMethod = RecordActionMethod;

/**
 * What has already been recorded from ONE resolution.
 *
 * **Two flags rather than one, because the two acts spend different things.** The scan's
 * idempotency key is consumed once in `presence_events` by a movement and once in `meal_events` by
 * a meal, on two partial unique indexes over two tables that cannot see each other — so recording
 * an entry spends nothing the meal path needs. A single "this resolution has acted" flag would mean
 * a resident arriving at breakfast time gets their entry recorded and then has to be scanned AGAIN
 * to be fed, with `scan.client_burst_dedupe_ms` swallowing the immediate rescan.
 *
 * It rides on the STAGE rather than beside it, so that every place that replaces the stage has to
 * decide what to carry forward: a `useState` of its own would be one somebody forgets to reset on
 * the next scan, which is a meal row disabled for the wrong person.
 */
interface Done {
  readonly movement: boolean;
  readonly meal: boolean;
}

/** Nothing recorded yet. The state every fresh resolution starts in. */
const NOTHING_DONE: Done = { movement: false, meal: false };

/**
 * What a stage has already recorded, defaulting to nothing.
 *
 * A function rather than a member read at the call site, because two of the stages carry no `done`
 * at all — `idle` and `resolving` are both "there is no resolution to have acted on" — and spelling
 * that out at each of the places that replace a stage is where one of them would get it wrong.
 *
 * At module scope rather than inside the component, so it is declared before every use and closes
 * over nothing.
 */
function doneOf(stage: Stage): Done {
  return stage.kind === 'resolved' || stage.kind === 'outcome' ? stage.done : NOTHING_DONE;
}

/** What the screen is showing. Each stage is one thing the operator is looking at. */
type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'resolving' }
  | { readonly kind: 'resolved'; readonly resolved: ResolveScanResponse; readonly done: Done }
  /** A finished scan: a confirmation, or a refusal that never reached a person. */
  | {
      readonly kind: 'outcome';
      readonly outcome: GateOutcome;
      readonly resolved: ResolveScanResponse | null;
      /**
       * The scan that produced this outcome, when it is still known.
       *
       * A refusal carries no person, so the band in the operator's hand exists nowhere else on this
       * screen once `resolveCode` returns — and `blank_unclaimed` is precisely the row whose next
       * step is "register the person this band belongs to". The METHOD travels with it rather than
       * being read back off `scanMethod` at render time: that ref outlives the outcome on screen, so
       * reading it later is both a lie waiting to happen and, in this project's lint configuration,
       * a ref read during render. `null` for an outcome reached from a SUBMISSION, which already has
       * a resolved person and offers no registration.
       */
      readonly scan: { readonly code: string; readonly method: ScanMethod } | null;
      readonly done: Done;
    };

/** What the anomaly dialog is open about, or `null` when it is closed. */
interface PendingAnomaly {
  readonly kind: AnomalyKind;
  readonly resolved: ResolveScanResponse;
  readonly direction: Direction;
  readonly method: ScanMethod;
  /** Why the last attempt from inside the dialog was refused, or `null`. */
  readonly failure: string | null;
}

/** What the meal-repeat dialog is open about, or `null` when it is closed. */
interface PendingRepeat {
  readonly resolved: ResolveScanResponse;
  readonly meal: Meal;
  readonly method: ScanMethod;
  /** Why the last attempt from inside the dialog was refused, or `null`. */
  readonly failure: string | null;
}

/** One serving, kept so that a transport failure can be retried under the same key (AD-30). */
interface MealAttempt {
  readonly resolved: ResolveScanResponse;
  readonly meal: Meal;
  readonly method: ScanMethod;
  readonly reasonCode: MealReasonCode | null;
}

/** One submission, kept so that a transport failure can be retried under the same key (AD-30). */
interface Attempt {
  readonly resolved: ResolveScanResponse;
  readonly direction: Direction;
  readonly method: ScanMethod;
  readonly decision: AnomalyDecision | null;
}

export function GateScreen({
  media = platformMedia(),
  storage = globalThis.localStorage,
  alarm,
  loadDetector,
}: Readonly<{
  /** `null` when this browser exposes no camera API at all. */
  media?: MediaDevicesLike | null;
  storage?: DeviceStorage;
  /** Injected, because `jsdom` has no Web Audio and an alarm that cannot be heard cannot be tested. */
  alarm?: Alarm;
  /** Injected, because `jsdom` runs no WebAssembly. */
  loadDetector?: () => Promise<LoadedDetector>;
}> = {}): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const queries = useQueryClient();
  const session = useSession();
  const navigate = useNavigate();
  const formatters = useFormatters();
  const describe = useApiErrorMessage();
  // A RECORDED refusal arrives on a 200 and carries a code rather than a thrown error, so it is
  // rendered from the catalogue the server would have used rather than from `describe`.
  const describeCode = useErrorCodeMessage();
  const { locale } = useLocale();
  // The sentence beneath the row, from one place, so the rate-limited hint and a refusal's own
  // message cannot disagree about where a detail comes from.
  const detailOf = useCallback(
    (outcome: GateOutcome): { detail?: string } => {
      const detail = outcomeDetail(outcome, locale, {
        retryAfter: (seconds) => t('errors.retryAfter', { retryAfterSeconds: seconds }),
      });
      return detail === null ? {} : { detail };
    },
    [locale, t],
  );

  const { scanDuplicateWindowMs } = useAppConfig();
  const [checkingScanner, setCheckingScanner] = useState(false);
  const scannerTrace = useScannerTrace();
  const fallbackAlarm = useMemo(() => createAlarm(browserAudio), []);
  const sound = alarm ?? fallbackAlarm;

  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [typed, setTyped] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [pending, setPending] = useState<PendingAnomaly | null>(null);
  const [repeat, setRepeat] = useState<PendingRepeat | null>(null);
  /**
   * The open repeat dialog, readable from inside an already-running serving.
   *
   * A ref beside the state for the reason `pendingRef` is one: the request that fails was started
   * before that state was read, and what it needs to know is whether the operator is looking at the
   * dialog right now.
   */
  const repeatRef = useRef<PendingRepeat | null>(null);
  useEffect(() => {
    repeatRef.current = repeat;
  }, [repeat]);
  /** The last serving, so a transport failure can be retried under the same key (AD-30). */
  const [lastMeal, setLastMeal] = useState<MealAttempt | null>(null);
  const [serving, setServing] = useState(false);
  /**
   * Why the last serving failed outside the dialog, or `null`.
   *
   * Rendered inline beside the meal row rather than as a gate outcome, for the reason `serve`
   * states: a serving does not enter the outcome stage at all.
   */
  const [mealFailure, setMealFailure] = useState<string | null>(null);
  /**
   * The open anomaly, readable from inside an already-running submission.
   *
   * A ref beside the state because the request that fails was started before that state was read,
   * and what it needs to know is whether the operator is looking at the dialog right now.
   */
  const pendingRef = useRef<PendingAnomaly | null>(null);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  /** The last submission, so a transport failure can be retried under the same key (AD-30). */
  const [lastAttempt, setLastAttempt] = useState<Attempt | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const codeFieldId = useId();

  const role = session?.user.role ?? null;
  const canModify = role !== null && roleHasPermission(role, 'person:write');

  /** How the code that produced the current resolution arrived, for the movement it becomes. */
  const scanMethod = useRef<ScanMethod>('scan_hardware');

  /**
   * One code, however it arrived.
   *
   * The alarm is primed FIRST and unconditionally, because this is the last moment inside the
   * operator's own gesture; everything after the first `await` is outside it as far as a browser's
   * autoplay policy is concerned.
   */
  const resolveCode = useCallback(
    (code: string, method: ScanMethod) => {
      sound.prime();
      scanMethod.current = method;
      setCameraOpen(false);
      // The previous scan's attempt is forgotten HERE, not when its outcome is cleared. A retry
      // button belongs to the submission on screen, and an offline RESOLVE renders one with no
      // resolution behind it — so without this, pressing it would re-send the last person's
      // movement under their spent key, answer `replayed` for somebody who is not standing there,
      // and write a `presence.duplicate_suppressed` row into the figure Section 1.12 documents as a
      // scanner-health signal.
      setLastAttempt(null);
      // The same reasoning, one domain along: a retry offered after a failed serving belongs to the
      // serving on screen, and the next scan is a different person.
      setLastMeal(null);
      setRepeat(null);
      // And the banner that described that serving. It is rendered as a SIBLING of the stage rather
      // than inside it, so without this it outlives the person it was about and greets the next
      // operator as an unexplained failure with no retry beside it.
      setMealFailure(null);
      setStage({ kind: 'resolving' });
      void (async () => {
        try {
          const resolved = await client.request(apiRoutes.presenceResolve, { body: { code } });
          setStage({ kind: 'resolved', resolved, done: NOTHING_DONE });
          setTyped('');
        } catch (error) {
          if (isSessionGateRefusal(error)) {
            // **The lock is not a gate outcome, and this screen must not draw it as one.** The
            // refusal is raised by the authorizer before the handler, so nothing was read and no
            // `scan_attempts` row was written; what it means is that this terminal is locked, which
            // the overlay over this screen is already saying. Rendering it as a refusal would leave
            // a red `role="alert"` banner quoting "enter your PIN" at a door whose PIN has since
            // been accepted. There is nothing resolved to keep here, so the screen goes quiet and
            // the operator scans again.
            setStage({ kind: 'idle' });
            return;
          }
          setStage({
            kind: 'outcome',
            outcome: outcomeOfFailure(error),
            resolved: null,
            scan: { code, method },
            done: NOTHING_DONE,
          });
        }
      })();
    },
    [client, sound],
  );

  const captureState = useScanCapture({
    enabled: true,
    // AD-30's window, as this installation has it set. Read from the configuration the browser
    // already fetches before its first render rather than from a constant, because an administrator
    // who changes `scan.client_burst_dedupe_ms` is changing the behaviour of this line. The value
    // cannot move underneath the capture: that configuration is fetched once before anything is
    // drawn and is constant for the life of the page, so a change reaches a terminal on its next
    // load rather than mid-shift, which is the same contract the issuance mode already has.
    duplicateWindowMs: scanDuplicateWindowMs,
    // Only while somebody is looking. Undefined otherwise, so an ordinary shift pays nothing for
    // it; and it travels through the hook's own ref, so opening the panel does not tear the
    // listeners down — which would drop a run halfway through, at exactly the moment somebody who
    // just opened it is about to scan.
    ...(checkingScanner ? { onObserve: scannerTrace.observe } : {}),
    onScan: (code, origin) => {
      // **The method is the capture's own evidence, not the path it arrived by.** A run is called a
      // device only when it showed a gap no hand produces AND finished faster than anybody types;
      // anything slower is an operator, and filing that as a hardware scan would quietly corrupt
      // AD-53's manual-versus-scan ratio in the direction nobody would ever notice. Widening the
      // capture so that a slow reader works is what makes this distinction necessary: before it,
      // everything the capture took was fast by construction.
      resolveCode(code, origin === 'device' ? 'scan_hardware' : 'manual_key');
    },
    onDuplicate: () => {
      // A hardware double read is one physical act and is suppressed at the input (AD-32). It is
      // deliberately silent: an operator who scanned once did nothing wrong, and a warning per
      // bounce would train them to ignore the one that matters.
    },
  });

  const record = useCallback(
    (attempt: Attempt) => {
      const { resolved, direction, method, decision } = attempt;
      setSubmitting(true);
      setLastAttempt(attempt);
      void (async () => {
        let answer: SubmitPresenceResponse;
        try {
          answer = await client.request(apiRoutes.presenceSubmit, {
            body: {
              personId: resolved.person.id,
              credentialId: resolved.credentialId,
              direction,
              method,
              ...(decision === null
                ? {}
                : {
                    reasonCode: decision.reasonCode,
                    ...(decision.reasonNote === null ? {} : { reasonNote: decision.reasonNote }),
                  }),
            },
            // AD-30: the key was minted by the RESOLVE, and a refusal does not consume it, so the
            // reasoned retry of a refused movement lands on the same key.
            idempotencyKey: resolved.idempotencyKey,
          });
        } catch (error) {
          const outcome = outcomeOfFailure(error);
          setSubmitting(false);
          const open = pendingRef.current;
          if (open !== null) {
            // The refusal came from INSIDE the dialog: the reason was one this actor may not file,
            // the network went away. It has to be read where the operator is looking, because a
            // modal overlay makes anything behind it unreachable — and the dialog is left exactly
            // as they filled it in, so pressing the button again is a retry of the same decision
            // under the same key rather than a second one.
            //
            // The message is the REAL one. It used to be collapsed to a single sentence, because
            // the field above it was somebody else's password and telling the two refusals apart
            // would have said which accounts could authorise; with no password here there is
            // nothing to enumerate and the honest message is strictly more useful.
            setPending({ ...open, failure: describe(error) });
            return;
          }
          if (outcome.kind === 'reason_required') {
            // The state moved between the resolve and the submission, which is ordinary at a busy
            // gate: somebody else recorded a movement for this person in between. Nothing was
            // written, and the operator is asked the question the server asked.
            setPending({ kind: 'contradiction', resolved, direction, method, failure: null });
            return;
          }
          if (isSessionGateRefusal(error)) {
            // **The person stays on screen, and so does their key.** `routes/guard.ts` runs the
            // authorizer before the handler, so a lock refusal means no `presence_events` row was
            // written and the idempotency key AD-30 minted at the RESOLVE was not consumed. Going
            // to `idle` here would throw away `resolved` and make the operator scan the band again
            // — a second resolve, a new key, and their place lost with somebody standing at the
            // door. Putting the stage back where it was means the direction buttons are there when
            // they come back from the PIN, and pressing one is the same submission under the same
            // key rather than a second one.
            setStage((current) => ({ kind: 'resolved', resolved, done: doneOf(current) }));
            return;
          }
          setStage((current) => ({
            kind: 'outcome',
            outcome,
            resolved,
            scan: null,
            done: doneOf(current),
          }));
          return;
        }

        const outcome = outcomeOfSubmission(answer);
        setSubmitting(false);
        // **The one write in this product that reaches the most views and used to declare none.**
        // A movement changes the occupancy card, the evacuation roll-call, the outside monitor, the
        // Live panel and that person's own record — and at a gate none of those is on screen, so
        // every one of them is a cache mark and zero requests (`invalidateQueries` refetches only
        // ACTIVE observers). What it buys is an administrator's dashboard that is correct while
        // they are looking at it. Declared beside the route in `api/invalidation.ts`; applied here
        // because this submission goes through the client directly rather than through
        // `useApiMutation`, for AD-30's key, which the resolve minted.
        applyWriteEffects(queries, apiRoutes.presenceSubmit, {
          params: {},
          body: { personId: resolved.person.id },
          result: answer,
        });
        if (outcome.kind === 'blocked') {
          // A 200, not an error: the refusal WAS recorded (Section 1.11's blocked row), and what
          // the operator has to supply now is a reason. The retry lands on the same key, because a
          // rejection did not consume it — which is what makes the trace and the movement two rows
          // of one decision rather than two decisions.
          setPending({ kind: 'blocked', resolved, direction, method, failure: null });
          setStage((current) => ({
            kind: 'outcome',
            outcome,
            resolved,
            scan: null,
            done: { ...doneOf(current), movement: true },
          }));
          return;
        }
        if (outcome.kind === 'duplicate') {
          // Section 1.11 and AD-31 word this exactly: "already registered N seconds ago", with the
          // original event's own time. Both halves are needed and neither replaces the other — the
          // elapsed seconds are how an operator tells their own double-press from somebody else's
          // scan five minutes ago, and the instant is what they quote.
          toast(
            t('scan.duplicate.toast', {
              seconds: secondsSince(outcome.occurredAt),
              time: formatters.time(outcome.occurredAt),
            }),
          );
        }
        setPending(null);
        setStage((current) => ({
          kind: 'outcome',
          outcome,
          resolved,
          scan: null,
          // The movement is spent whatever the outcome says: every branch that reaches here wrote a
          // `presence_events` row, which is what consumed the key. A meal from the same scan is
          // untouched, because it spends a different table.
          done: { ...doneOf(current), movement: true },
        }));
      })();
    },
    [client, describe, formatters, queries, t],
  );

  /**
   * Sends one serving, and renders its outcome IN PLACE.
   *
   * **A meal never enters the `outcome` stage**, and that is decided here rather than left to
   * discovery. `GateOutcome` is a closed union with a total treatments record, a tone map and a
   * `scan.outcome.<kind>.*` catalogue subtree; extending all of that for a routine second helping
   * would add kinds, tones and strings in two languages to the EAGERLY loaded chunk for a
   * confirmation the panel can already show. What confirms a serving is the button flipping to a
   * tick and the time beneath it, which is the same information a banner would have carried.
   *
   * The answer's `dining` block REPLACES the resolution's, which is not optional: the panel stays
   * on screen, so without the merge the button an operator just pressed would go on saying the meal
   * had not been taken, directly beneath the tick saying it had.
   */
  const serve = useCallback(
    (attempt: MealAttempt) => {
      const { resolved, meal, method, reasonCode } = attempt;
      setServing(true);
      setLastMeal(attempt);
      setMealFailure(null);
      void (async () => {
        let answer: SubmitMealResponse;
        try {
          answer = await client.request(apiRoutes.diningSubmit, {
            body: {
              personId: resolved.person.id,
              credentialId: resolved.credentialId,
              meal,
              method,
              ...(reasonCode === null ? {} : { reasonCode }),
            },
            // AD-30: the key the RESOLVE minted, shared with the movement. A refusal does not
            // consume it, so the reasoned retry of a refused serving lands on the same key.
            idempotencyKey: resolved.idempotencyKey,
          });
        } catch (error) {
          setServing(false);
          const open = repeatRef.current;
          if (open !== null) {
            // The refusal came from INSIDE the dialog, which is where the operator is looking: a
            // modal overlay makes anything behind it unreachable. The dialog keeps the reason they
            // chose, so pressing again is a retry of the same decision under the same key.
            setRepeat({ ...open, failure: describe(error) });
            return;
          }
          if (hasCode(error, 'DINING_REASON_REQUIRED')) {
            // The race: another counter served this person between the resolve and the press. The
            // browser normally pre-empts this, so reaching it means the world moved. Nothing was
            // written and the key is unspent, so the operator is asked the question the server
            // asked.
            setRepeat({ resolved, meal, method, failure: null });
            return;
          }
          if (isSessionGateRefusal(error)) {
            // Nothing was written and the key was not consumed, exactly as on the movement path:
            // the authorizer runs before the handler. The panel stays, so pressing again after the
            // PIN is the same serving under the same key rather than a second one.
            return;
          }
          setMealFailure(describe(error));
          return;
        }

        setServing(false);
        setRepeat(null);
        setLastMeal(null);
        applyWriteEffects(queries, apiRoutes.diningSubmit, {
          params: {},
          body: { personId: resolved.person.id },
          result: answer,
        });
        // **A RECORDED refusal is not a serving, and it arrives on a 200.** `submit` answers `ok`
        // for everything the matrix can decide, so an archived record comes back as a `rejected`
        // row with `PERSON_ARCHIVED` on `refusal` rather than as a thrown error. Reading only the
        // status would tell the operator their meal was served when a row says it was refused.
        //
        // It is not reachable from a panel that was drawn a moment ago, because `permittedMeals`
        // is empty for the only record that produces one — but it IS reachable when the record is
        // archived between the resolve and the press, which is the whole reason the server sends
        // it. The banner says what happened; the day's state below is still replaced, because the
        // server's answer is the truth about it either way.
        const refusal = answer.refusal;
        if (refusal !== null) {
          // Asked rather than cast: `recordedRefusalSchema.code` is a bounded STRING on the wire,
          // because the refusal travels on a success body rather than through the error contract.
          // A code this browser does not know would render its own name, which is worse than the
          // generic sentence and is the one case `describe` exists for.
          setMealFailure(
            isErrorCode(refusal.code) ? describeCode(refusal.code) : t('errors.unexpectedBody'),
          );
        }
        // In place, and never a stage change: see the note above. The `dining` block is replaced
        // wholesale because the server sent the whole day's state for exactly this.
        setStage((current) => {
          if (current.kind !== 'resolved' && current.kind !== 'outcome') {
            return current;
          }
          if (current.resolved === null) {
            return current;
          }
          // **The answer belongs to the person it was SENT for, not to whoever is on screen now.**
          // Nothing withdraws the code field or the scan capture while a serving is in flight, so
          // an operator can scan somebody else before this resolves. Without this guard the merge
          // writes one person's sittings onto another's panel and marks THEM fed — the second
          // person is shown a tick they never earned, their buttons go, and they walk away unfed
          // with no row anywhere saying so. The request itself carried the right id, so nothing
          // wrong was ever written; this is the screen catching up with that.
          if (current.resolved.person.id !== resolved.person.id) {
            return current;
          }
          // One spread for both stages, because the patch IS the same: whichever the operator is
          // looking at, what changed is the day's state and the fact that this resolution has now
          // fed somebody. The discriminant is preserved by the spread.
          return {
            ...current,
            resolved: { ...current.resolved, dining: answer.dining },
            // A refusal fed nobody, so it does not spend this resolution's serving.
            done: { ...current.done, meal: current.done.meal || refusal === null },
          };
        });
      })();
    },
    [client, describe, describeCode, queries, t],
  );

  /**
   * Serving one sitting, pre-empting a repeat before anything is sent.
   *
   * **The repeat is pre-empted and a movement's contradiction is too**, which is the same rule one
   * domain along: the scan already told this screen what the person has eaten, so a press on a
   * taken sitting is a deliberate request for a further helping rather than a mistake to record.
   * Asking first means a misclick writes nothing at all.
   */
  const serveMeal = useCallback(
    (resolved: ResolveScanResponse, meal: Meal) => {
      sound.prime();
      const taken = resolved.dining?.meals.find((state) => state.meal === meal)?.served === true;
      if (taken) {
        setRepeat({ resolved, meal, method: scanMethod.current, failure: null });
        return;
      }
      serve({ resolved, meal, method: scanMethod.current, reasonCode: null });
    },
    [serve, sound],
  );

  const submit = useCallback(
    (resolved: ResolveScanResponse, direction: Direction) => {
      sound.prime();

      // **Only a contradiction is pre-empted, and the asymmetry is Section 1.11's own.** A
      // contradicting direction with no reason writes NO row — the server answers
      // `PRESENCE_REASON_REQUIRED` and nothing happens — so asking first is what keeps a refusal
      // nobody made out of the table the anomalies panel counts. A BLOCKED person is the opposite:
      // their refusal is a fact about the world and the matrix records it as `rejected`, so the
      // submission goes first and the dialog opens from the answer. Asking first there would mean
      // an operator who pressed "record nothing" left no trace of somebody blocked presenting a
      // band at a door.
      const blocked = resolved.warnings.includes('person_blocked');
      if (direction !== resolved.expectedDirection && !blocked) {
        setPending({
          kind: 'contradiction',
          resolved,
          direction,
          method: scanMethod.current,
          failure: null,
        });
        return;
      }
      record({ resolved, direction, method: scanMethod.current, decision: null });
    },
    [record, sound],
  );

  /**
   * Leaving for the register, with what the gate knows carried in memory rather than in the URL.
   *
   * Both halves of AD-28 apply: a pass code is a live bearer token and a person id names somebody,
   * and `features/persons/table/url-state.ts` already refuses the address bar for a search term on
   * the ground that it is a file on a shared terminal's disk. `features/scan/handoff.ts` states why
   * `navigate(path, { state })` is not an alternative.
   */
  const registerFrom = useCallback(
    (code: string | null, method: ScanMethod) => {
      stageIntake({ kind: 'register', code, method });
      void navigate('/persons');
    },
    [navigate],
  );

  const openRecord = useCallback(
    (personId: string, editing: boolean) => {
      // The method the SCAN arrived by, so the `person.view_full` row AD-53 counts says a scanner
      // reached this record rather than a search box.
      stageIntake({ kind: 'record', personId, editing, method: scanMethod.current });
      void navigate('/persons');
    },
    [navigate],
  );

  const typedParse = parsePassCode(typed);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('scan.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('scan.subtitle')}</p>
        </div>
        <ScanIndicator state={captureState} />
      </header>

      <section className="grid gap-3 rounded-lg border bg-card p-4">
        <div className="grid gap-1">
          <Label htmlFor={codeFieldId}>{t('scan.code.label')}</Label>
          <div className="flex gap-2">
            <Input
              id={codeFieldId}
              data-testid="scan-code"
              // AD-32: the dedicated code field owns the keyboard, so a scan into it is taken
              // directly rather than intercepted and replayed.
              {...{ [SCAN_OPT_OUT_ATTRIBUTE]: SCAN_OPT_OUT }}
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              onKeyDown={(event) => {
                // `Tab` as well as `Enter`, because a reader configured with a Tab suffix aimed at
                // this field would otherwise leave the code sitting here while focus moved on.
                // Only when the value is already a complete code, so an incomplete one still tabs
                // away normally, and never on `Shift+Tab`, which is backwards navigation.
                //
                // **`Enter` is cancelled and `Tab` is NOT**, and the asymmetry is deliberate.
                // Cancelling `Enter` stops the form submit that would otherwise follow it;
                // cancelling `Tab` would hijack the key out of a text input, on the one screen
                // `test:a11y` walks by keyboard, to prevent a focus move that costs nothing. So a
                // `Tab` submits AND moves focus, which is what the operator asked for either way.
                if (typedParse.ok && event.key === 'Enter') {
                  event.preventDefault();
                  resolveCode(typedParse.code, 'manual_key');
                } else if (typedParse.ok && event.key === 'Tab' && !event.shiftKey) {
                  resolveCode(typedParse.code, 'manual_key');
                }
              }}
            />
            <Button
              type="button"
              data-testid="scan-check"
              disabled={!typedParse.ok}
              onClick={() => {
                if (typedParse.ok) {
                  resolveCode(typedParse.code, 'manual_key');
                }
              }}
            >
              {t('scan.code.submit')}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{t('scan.code.hint')}</p>
          {typed.length > 0 && !typedParse.ok && (
            <p className="text-sm text-warning">{t('scan.code.malformed')}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // Inside the operator's own gesture, which is the only moment a browser will let a
              // page start audio.
              sound.prime();
              setCameraOpen((open) => !open);
            }}
          >
            {cameraOpen ? <CameraOff aria-hidden /> : <Camera aria-hidden />}
            {t(cameraOpen ? 'scan.camera.close' : 'scan.camera.open')}
          </Button>

          {/*
            Starting a registration without leaving the gate.

            It is not a convenience: `CREDENTIAL_BLANK_UNCLAIMED` never reaches an installation that
            mints its own codes (`credentials/resolve.ts` maps it to "not recognised" there, because
            the other sentence would describe a step that does not exist), so without this control
            that mode gets nothing from the gate-first intake at all. It is a way IN to the
            registration screen rather than a way past the band: a pre-printed installation requires
            one, so the form refuses until a band is scanned into it or an administrator has turned
            on the break-glass. What this saves is the walk back to the register.

            The method is `manual_key` rather than whatever the last scan was. `scanMethod` is a ref
            that survives the outcome on screen, so reading it here would file a registration nobody
            scanned as a hardware scan, and nothing would ever say so.
          */}
          {canModify && (
            <Button
              type="button"
              variant="outline"
              data-testid="gate-register"
              onClick={() => {
                registerFrom(null, 'manual_key');
              }}
            >
              <UserRoundPlus aria-hidden />
              {t('person.table.newPerson')}
            </Button>
          )}
        </div>

        {cameraOpen && (
          <ScanCamera
            media={media}
            storage={storage}
            {...(loadDetector === undefined ? {} : { loadDetector })}
            onCode={(code) => {
              resolveCode(code, 'scan_camera');
            }}
          />
        )}

        <ScannerCheck
          trace={scannerTrace.trace}
          open={checkingScanner}
          onToggle={() => {
            setCheckingScanner((open) => !open);
          }}
          onClear={scannerTrace.clear}
        />

        <ScanUpload
          {...(loadDetector === undefined ? {} : { loadDetector })}
          onCode={(code) => {
            resolveCode(code, 'scan_upload');
          }}
        />
      </section>

      {stage.kind === 'resolving' && (
        <p role="status" className="text-sm text-muted-foreground">
          {t('common.loading')}
        </p>
      )}

      {stage.kind === 'resolved' && (
        <ResolvePanel
          resolved={stage.resolved}
          submitting={submitting || serving}
          canModify={canModify}
          movable={!stage.done.movement}
          servable={!stage.done.meal}
          onSubmit={(direction) => {
            submit(stage.resolved, direction);
          }}
          onServe={(meal) => {
            serveMeal(stage.resolved, meal);
          }}
          onView={() => {
            openRecord(stage.resolved.person.id, false);
          }}
          onModify={() => {
            openRecord(stage.resolved.person.id, true);
          }}
        />
      )}

      {stage.kind === 'outcome' && (
        <div className="grid gap-3">
          <GateStatusBanner outcome={stage.outcome} {...detailOf(stage.outcome)} />
          {/*
            AD-44 and Section 1.11's last row: a submission that never reached the server is safe to
            retry, because the key was minted before the attempt. Without a retry the only way
            forward is a new scan, which mints a NEW key — so a movement that committed and whose
            answer was lost becomes a second, contradicting movement recorded as an override: a
            fabricated anomaly for something that had already happened correctly.
          */}
          {stage.outcome.kind === 'offline' && lastAttempt !== null && (
            <Button
              type="button"
              disabled={submitting}
              onClick={() => {
                record(lastAttempt);
              }}
            >
              {t('common.retry')}
            </Button>
          )}
          {/*
            The band belongs to nobody yet, and this is the one row of Section 1.11 whose next step
            is a registration. The operator presses one button, arrives at the form with this code
            already in the band field, and comes back here when the record is closed.

            It is a SIBLING of the banner rather than a child: that banner's own element is the
            `role="status"` live region, and a control inside it would be read out again on every
            outcome. `canModify` is the same `person:write` check the modify action uses, so an
            operator who would be refused at `person.create` is never offered the walk.

            No issuance-mode branch, and that is not an omission: the server answers
            `CREDENTIAL_BLANK_UNCLAIMED` only where registration against a blank is possible, and
            reports the very same band as "not recognised" in the other mode. The outcome IS the
            mode signal.
          */}
          {stage.outcome.kind === 'blank_unclaimed' && stage.scan !== null && canModify && (
            <RegisterBandAction scan={stage.scan} onRegister={registerFrom} />
          )}
          {/*
            The person stays on screen after a recorded movement, because the next thing an
            operator does is look at the face in front of them and, sometimes, record the other
            direction. An anomaly is the exception: its own alert is what has to be read.
          */}
          {stage.resolved !== null && !isAnomaly(stage.outcome) && (
            <ResolvedAgain
              resolved={stage.resolved}
              canModify={canModify}
              submitting={submitting || serving}
              done={stage.done}
              onView={openRecord}
              onSubmit={(direction) => {
                if (stage.resolved !== null) {
                  submit(stage.resolved, direction);
                }
              }}
              onServe={(meal) => {
                if (stage.resolved !== null) {
                  serveMeal(stage.resolved, meal);
                }
              }}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setMealFailure(null);
              setStage({ kind: 'idle' });
            }}
          >
            {t('scan.person.another')}
          </Button>
        </div>
      )}

      {/*
        A serving that failed outside the dialog, said where the operator is looking. It is a
        SIBLING of the panel rather than a gate outcome, because a meal never enters that stage:
        see `serve`. The retry re-sends under the same key, which a refusal did not consume.
      */}
      {mealFailure !== null && (
        <div className="grid gap-2">
          <p
            role="alert"
            data-testid="meal-failure"
            className="rounded-md border border-danger p-3 text-sm text-danger"
          >
            {mealFailure}
          </p>
          {lastMeal !== null && (
            <Button
              type="button"
              data-testid="meal-retry"
              disabled={serving}
              onClick={() => {
                serve(lastMeal);
              }}
            >
              {t('scan.meal.retry')}
            </Button>
          )}
        </div>
      )}

      {/*
        Mounted only while there IS a repeat to decide about, for the reason the anomaly dialog is:
        a dialog kept mounted and toggled would carry one operator's reason into the next person's
        meal.
      */}
      {repeat !== null && (
        <MealRepeatDialog
          meal={repeat.meal}
          servedAtLabel={(() => {
            // Built at the CALL SITE rather than inside the dialog, so the dialog takes a string
            // exactly as `DirectionButton` takes its label: `test:i18n` parses `t()` where it is
            // written, and a component that took a catalogue key would move the sentence away from
            // the screen that owns it.
            const at =
              repeat.resolved.dining?.meals.find((state) => state.meal === repeat.meal)?.at ?? null;
            return at === null
              ? t('scan.meal.notServed')
              : t('scan.meal.servedAt', { time: formatters.time(at) });
          })()}
          submitting={serving}
          failure={repeat.failure}
          onConfirm={(reasonCode) => {
            serve({
              resolved: repeat.resolved,
              meal: repeat.meal,
              method: repeat.method,
              reasonCode,
            });
          }}
          onCancel={() => {
            setRepeat(null);
          }}
        />
      )}

      {/*
        Mounted only while there IS an anomaly, which is what makes every field in it fresh: see
        the note on `AnomalyDialog`. A dialog kept mounted and toggled would carry one operator's
        reason and explanation into the next person's movement.
      */}
      {pending !== null && (
        <AnomalyDialog
          open
          kind={pending.kind}
          presenceState={pending.resolved.person.presenceState}
          alarm={sound}
          submitting={submitting}
          failure={pending.failure}
          onConfirm={(decision) => {
            // The method the SCAN arrived by, carried on the pending anomaly rather than read back
            // off the ref: what is recorded is how this movement reached the terminal, and AD-53's
            // manual-versus-scan ratio is only worth anything if that stays true.
            record({
              resolved: pending.resolved,
              direction: pending.direction,
              method: pending.method,
              decision,
            });
          }}
          onCancel={() => {
            setPending(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The person, still on screen after an act was recorded, with whatever is LEFT to press.
 *
 * The operator's next act is to look at the face in front of them, so the record stays. What it may
 * still offer is decided by {@link Done} rather than by a hard-coded `false`: the idempotency key
 * belongs to the SCAN (AD-30) and is spent once in `presence_events` and once in `meal_events`, on
 * two partial unique indexes over two tables that cannot see each other. So a second direction
 * pressed after a movement would be answered as a replay and is withdrawn — but a MEAL after that
 * movement spends a key nothing has touched, and withdrawing it would make a resident arriving at
 * breakfast time need a second scan to be fed.
 */
function ResolvedAgain({
  resolved,
  canModify,
  submitting,
  done,
  onView,
  onSubmit,
  onServe,
}: Readonly<{
  resolved: ResolveScanResponse;
  canModify: boolean;
  submitting: boolean;
  /** What this resolution has already recorded. Each half withdraws only its own row. */
  done: Done;
  /** Opens this person's record, for reading or for amendment. */
  onView: (personId: string, editing: boolean) => void;
  onSubmit: (direction: Direction) => void;
  onServe: (meal: Meal) => void;
}>): ReactNode {
  return (
    <ResolvePanel
      resolved={resolved}
      submitting={submitting}
      canModify={canModify}
      movable={!done.movement}
      servable={!done.meal}
      onSubmit={onSubmit}
      onServe={onServe}
      // Two callbacks rather than one. This panel used to hand the same function to both, so
      // "modify" opened the record for READING — a second, quieter copy of the defect that the
      // whole of this hand-off exists to remove.
      onView={() => {
        onView(resolved.person.id, false);
      }}
      onModify={() => {
        onView(resolved.person.id, true);
      }}
    />
  );
}

/**
 * The one control that turns a band nobody has registered into a registration.
 *
 * A component of its own so the code narrows at the prop position rather than inside a closure, and
 * so the one thing it must never do — render when there is no code — is a type error rather than a
 * convention.
 */
function RegisterBandAction({
  scan,
  onRegister,
}: Readonly<{
  /** The band, and how it was read, which the registration's own audit row records (AD-53). */
  scan: { readonly code: string; readonly method: ScanMethod };
  onRegister: (code: string, method: ScanMethod) => void;
}>): ReactNode {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      data-testid="register-band"
      onClick={() => {
        onRegister(scan.code, scan.method);
      }}
    >
      <UserRoundPlus aria-hidden />
      {t('scan.intake.registerBand')}
    </Button>
  );
}

/**
 * How long ago an instant was, in whole seconds, never negative.
 *
 * Clamped at zero because a terminal's clock and the installation's can disagree by a second or
 * two, and "recorded -1 seconds ago" reads as a fault in the product rather than in a clock.
 */
function secondsSince(instant: string): number {
  return Math.max(0, Math.round((Date.now() - Date.parse(instant)) / 1000));
}
